import { TFunction } from 'i18next';
import { currencyFormatter } from '../../../services/helperService';
import { CategoryRevenueItem, TopRevenueItem } from '../../../utils/revenueHistoryCalculations';
import { AddOnAggregation } from '../../../utils/revenueAddOns';

/**
 * Export helpers for the Revenue Items table: clipboard (TSV + JSON), a markdown .txt
 * file, and the column contract the .xlsx builder reuses.
 *
 * The rows handed in are exactly the rows the table is showing for the active view —
 * all filters, the search query and the sort are already applied by the page, so the
 * export never re-derives or re-filters data. Pagination is display-only, so every
 * filtered row is exported, not just the page on screen.
 *
 * This module is imported statically by the page, so it must stay free of heavy
 * dependencies — exceljs lives in the dynamically-imported `revenueItemsExcelExport.ts`.
 */

export type RevenueItemsView = 'items' | 'category' | 'addons';

export type ItemsExportRow = TopRevenueItem & { rank: number; percentageOfTotal: number };
export type CategoryExportRow = CategoryRevenueItem & { rank: number };
/** `addOnCategory` is the choice's add-on category name, '' when unassigned. */
export type AddOnsExportRow = AddOnAggregation & { rank: number; addOnCategory?: string };
export type RevenueItemsExportRow = ItemsExportRow | CategoryExportRow | AddOnsExportRow;

export interface RevenueItemsExportContext {
  /** Empty in the All Restaurants view. */
  restaurantName: string;
  /** Human-readable period the rows cover, e.g. "1-7-2026 – 30-7-2026". */
  periodLabel: string;
  /** The page's overall-type filter, already translated. */
  typeFilterLabel: string;
  /** Which table view is on screen — decides the columns. */
  view: RevenueItemsView;
  showExVat: boolean;
  searchQuery: string;
  activeFilterCount: number;
  /**
   * Whether to export the menu-category column. Mirrors the table, which renders that
   * cell empty in the All Restaurants view (no single restaurant's cost price to read).
   */
  includeMenuCategory: boolean;
  /** The two KPI numbers above the table — exported as the total row. */
  totalRevenue: number;
  totalItems: number;
  generatedAt: Date;
}

export type RevenueExportCellType = 'text' | 'number' | 'currency' | 'percent';

export interface RevenueExportColumn {
  /** Stable English field name — used as the JSON key, never shown to the user. */
  key: string;
  /** Translated header for TSV / .txt / Excel. */
  label: string;
  type: RevenueExportCellType;
  /** Excel column width. */
  width: number;
  /** Raw (unformatted) value, so each format can render it its own way. */
  value: (row: any) => string | number;
}

// ---- Columns -----------------------------------------------------------------

/**
 * The active view's columns, in table order. Percentages are carried as 0–100 (what the
 * table holds); the Excel writer scales them to a fraction for its percent format.
 */
export function buildRevenueItemsColumns(t: TFunction, ctx: RevenueItemsExportContext): RevenueExportColumn[] {
  const rank: RevenueExportColumn = {
    key: 'rank',
    label: t('revenueHistory.export.rank'),
    type: 'number',
    width: 6,
    value: (row) => row.rank ?? '',
  };
  const share: RevenueExportColumn = {
    key: 'shareOfTotal',
    label: t('revenueHistory.percentOfTotal'),
    type: 'percent',
    width: 12,
    value: (row) => row.percentageOfTotal ?? 0,
  };

  if (ctx.view === 'category') {
    return [
      rank,
      { key: 'category', label: t('revenueHistory.category'), type: 'text', width: 32, value: (row) => row.categoryName ?? '' },
      { key: 'itemsSold', label: t('revenueHistory.items'), type: 'number', width: 12, value: (row) => row.itemSold ?? 0 },
      { key: 'avgItemPrice', label: t('revenueHistory.export.avgItemPrice'), type: 'currency', width: 16, value: (row) => row.avgItemPrice ?? 0 },
      { key: 'totalRevenue', label: t('revenueHistory.totalRevenue'), type: 'currency', width: 16, value: (row) => row.totalRevenue ?? 0 },
      share,
    ];
  }

  if (ctx.view === 'addons') {
    return [
      rank,
      { key: 'addOn', label: t('revenueHistory.addOns.tableName'), type: 'text', width: 42, value: (row) => row.name ?? '' },
      // Same All Restaurants rule as the menu-category column below: the table leaves
      // the cell empty there, so the export drops the column entirely.
      ...(ctx.includeMenuCategory
        ? [{
            key: 'addOnCategory',
            label: t('revenueHistory.category'),
            type: 'text' as const,
            width: 24,
            value: (row: any) => row.addOnCategory || t('common.unassigned'),
          }]
        : []),
      { key: 'quantity', label: t('revenueHistory.addOns.tableQuantity'), type: 'number', width: 12, value: (row) => row.quantity ?? 0 },
      { key: 'revenue', label: t('revenueHistory.addOns.tableRevenue'), type: 'currency', width: 16, value: (row) => row.revenue ?? 0 },
    ];
  }

  return [
    rank,
    // `displayName` so a partner-venue row exports the same "<item> — <venue>" the table shows.
    { key: 'product', label: t('revenueHistory.product'), type: 'text', width: 42, value: (row) => row.displayName ?? row.product ?? '' },
    ...(ctx.includeMenuCategory
      ? [{
          key: 'menuCategory',
          label: t('revenueHistory.category'),
          type: 'text' as const,
          width: 24,
          value: (row: any) => row.menuCategory ?? '',
        }]
      : []),
    { key: 'itemsSold', label: t('revenueHistory.items'), type: 'number', width: 12, value: (row) => row.items ?? 0 },
    { key: 'price', label: t('revenueHistory.price'), type: 'currency', width: 12, value: (row) => row.price ?? 0 },
    { key: 'totalRevenue', label: t('revenueHistory.totalRevenue'), type: 'currency', width: 16, value: (row) => row.totalPrice ?? 0 },
    share,
  ];
}

/**
 * The total row's value for a column, or '' where a total is meaningless (a summed
 * rank, average price or share would be nonsense). Quantities and revenue come from
 * the page's KPI numbers so the row always matches the cards above the table.
 * Shared with the Excel builder so both files show the same total row.
 */
export function revenueItemsTotalCell(column: RevenueExportColumn, ctx: RevenueItemsExportContext): string | number {
  if (column.key === 'itemsSold' || column.key === 'quantity') return ctx.totalItems;
  if (column.key === 'totalRevenue' || column.key === 'revenue') return ctx.totalRevenue;
  return '';
}

// ---- Cell formatting ---------------------------------------------------------

const formatCell = (column: RevenueExportColumn, raw: string | number): string => {
  if (raw === '' || raw == null) return '';
  switch (column.type) {
    case 'currency':
      return currencyFormatter.format(Number(raw));
    case 'percent':
      return `${Number(raw).toFixed(1)}%`;
    default:
      return String(raw);
  }
};

/** Tabs/newlines inside a cell would break the column alignment when pasted. */
const escapeTsvCell = (value: string): string => value.replace(/[\t\r\n]+/g, ' ');

/** Pipes break the markdown table this cell sits in; tabs/newlines mangle its layout. */
const escapeMdCell = (value: string): string => value.replace(/\|/g, '\\|').replace(/[\t\r\n]+/g, ' ');

const round = (value: number, decimals: number): number =>
  Number.isFinite(value) ? Number(value.toFixed(decimals)) : 0;

/** Currency/percent as numbers with sane precision; text untouched. */
function jsonValue(column: RevenueExportColumn, raw: string | number): string | number | null {
  if (raw === '' || raw == null) return column.type === 'text' ? '' : null;
  if (column.type === 'currency') return round(Number(raw), 2);
  if (column.type === 'percent') return round(Number(raw), 1);
  if (column.type === 'number') return Number(raw);
  return String(raw);
}

// ---- Emitters ----------------------------------------------------------------

/**
 * Tab-separated: header + one line per row, no metadata, so it pastes straight into
 * Excel / Google Sheets (and into a prompt) as a clean table. The JSON copy carries
 * the period/VAT context for cases where that matters.
 */
export function buildRevenueItemsClipboardText(
  t: TFunction,
  rows: RevenueItemsExportRow[],
  ctx: RevenueItemsExportContext
): string {
  const columns = buildRevenueItemsColumns(t, ctx);
  const lines = [columns.map((c) => escapeTsvCell(c.label)).join('\t')];
  for (const row of rows) {
    lines.push(columns.map((c) => escapeTsvCell(formatCell(c, c.value(row)))).join('\t'));
  }
  return lines.join('\n');
}

/**
 * JSON with the filter context in the envelope and raw numbers in the rows — the shape
 * to hand an LLM. Field names are deliberately English and untranslated (machine-facing).
 *
 * Items rows additionally nest their add-ons breakdown when they have one: it is the same
 * data the table's expanded row shows, and knowing a dish has build-your-own choices
 * matters when categorising a menu.
 */
export function buildRevenueItemsJson(
  t: TFunction,
  rows: RevenueItemsExportRow[],
  ctx: RevenueItemsExportContext
): string {
  const columns = buildRevenueItemsColumns(t, ctx);

  return JSON.stringify(
    {
      export: 'revenueItems',
      view: ctx.view,
      restaurant: ctx.restaurantName || null,
      period: ctx.periodLabel,
      typeFilter: ctx.typeFilterLabel,
      vat: ctx.showExVat ? 'excl' : 'incl',
      search: ctx.searchQuery || null,
      activeFilters: ctx.activeFilterCount,
      generatedAt: ctx.generatedAt.toISOString(),
      rowCount: rows.length,
      totals: { itemsSold: ctx.totalItems, revenue: round(ctx.totalRevenue, 2) },
      rows: rows.map((row) => {
        const out: Record<string, unknown> = {};
        for (const column of columns) {
          out[column.key] = jsonValue(column, column.value(row));
        }
        const addOns = (row as ItemsExportRow).addOnsBreakdown;
        if (ctx.view === 'items' && addOns && addOns.length > 0) {
          out.addOns = addOns.map((a) => ({
            name: a.name,
            quantity: a.quantity,
            revenue: round(a.revenue, 2),
          }));
        }
        return out;
      }),
    },
    null,
    2
  );
}

/**
 * Markdown .txt (same style as the inventory count export): a metadata block stating
 * exactly which slice of data this is, then one pipe table plus a total row.
 */
export function buildRevenueItemsTxt(
  t: TFunction,
  rows: RevenueItemsExportRow[],
  ctx: RevenueItemsExportContext
): string {
  const columns = buildRevenueItemsColumns(t, ctx);
  const parts: string[] = [];

  const title = t('revenueHistory.export.title');
  parts.push(ctx.restaurantName ? `# ${title} — ${ctx.restaurantName}` : `# ${title}`);
  parts.push('');
  parts.push(`- ${t('revenueHistory.export.view')}: ${viewLabel(t, ctx.view)}`);
  parts.push(`- ${t('revenueHistory.export.period')}: ${ctx.periodLabel}`);
  parts.push(`- ${t('revenueHistory.export.typeFilter')}: ${ctx.typeFilterLabel}`);
  parts.push(`- ${t('revenueHistory.export.vat')}: ${ctx.showExVat ? t('revenueHistory.exclVat') : t('revenueHistory.inclVat')}`);
  parts.push(`- ${t('revenueHistory.export.activeFilters')}: ${ctx.activeFilterCount}`);
  if (ctx.searchQuery) parts.push(`- ${t('revenueHistory.export.search')}: "${ctx.searchQuery}"`);
  parts.push(`- ${t('revenueHistory.export.rows')}: ${rows.length}`);
  parts.push(`- ${t('revenueHistory.export.generated')}: ${ctx.generatedAt.toLocaleString()}`);
  parts.push('');
  parts.push(`## ${viewLabel(t, ctx.view)}`);
  parts.push('');

  if (rows.length === 0) {
    parts.push(`_${t('revenueHistory.noItemsThisPeriod')}_`);
    return parts.join('\n');
  }

  const cells = (values: string[]) => `| ${values.map(escapeMdCell).join(' | ')} |`;
  parts.push(cells(columns.map((c) => c.label)));
  parts.push(`| ${columns.map(() => '---').join(' | ')} |`);
  for (const row of rows) {
    parts.push(cells(columns.map((c) => formatCell(c, c.value(row)))));
  }
  parts.push(
    cells(
      columns.map((c, i) =>
        i === 0 ? t('revenueHistory.export.totalRow') : formatCell(c, revenueItemsTotalCell(c, ctx))
      )
    )
  );

  return parts.join('\n');
}

/** Translated name of a table view, used in file headers and the export menu. */
export function viewLabel(t: TFunction, view: RevenueItemsView): string {
  if (view === 'category') return t('revenueHistory.export.viewCategory');
  if (view === 'addons') return t('revenueHistory.addOns.viewToggle');
  return t('revenueHistory.export.viewItems');
}

/** Filename: revenue-items-<view>-<restaurant-slug>-<yyyy-mm-dd>.<ext> */
export function revenueItemsExportFilename(
  ctx: RevenueItemsExportContext,
  extension: 'txt' | 'json' | 'xlsx'
): string {
  const slug = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  const date = ctx.generatedAt;
  const stamp = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const restaurant = slug(ctx.restaurantName);
  return ['revenue-items', ctx.view, restaurant || null, stamp].filter(Boolean).join('-') + `.${extension}`;
}

/** Trigger a browser download of a text file (.txt / .json). */
export function downloadTextFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

